import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Phone, MapPin, Edit3, Check, X, LogOut, ChevronRight, History } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtMonth as fmtDate } from "../lib/activity";
import EmptyState from "../components/EmptyState";
import { toast } from "sonner";

const EXIT_REASON_LABEL = {
  group_deal: "We made a group deal",
  better_deal: "I found a better deal elsewhere",
  no_longer: "I no longer intend to purchase",
};

function ActiveRow({ it, i }) {
  const cityShort = (it.city || "").split(",")[0].trim();
  return (
    <div className="flex items-center gap-3 px-4 py-3.5 bg-card" data-testid={`profile-active-row-${i}`}>
      <span className={`w-1 self-stretch shrink-0 ${it.unread_count ? "bg-signal" : "bg-[#FFBE06]"}`} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="font-bold truncate">{it.product_name}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {cityShort} · buying {fmtDate(it.date)} · <strong className="text-foreground num">✊ {it.bhega_count}</strong> united
          {it.unread_count > 0 && <span className="text-signal font-bold"> · {it.unread_count} new message{it.unread_count === 1 ? "" : "s"}</span>}
        </div>
      </div>
      <Link to={`/${it.product_slug || it.product_id}`} className="btn-primary text-xs shrink-0" data-testid={`profile-active-room-${i}`}>
        Open room <ChevronRight size={13} />
      </Link>
    </div>
  );
}

function ExitedRow({ it, i }) {
  const cityShort = (it.city || "").split(",")[0].trim();
  const reasonLabel = EXIT_REASON_LABEL[it.reason] || it.reason || "—";
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-card" data-testid={`profile-exited-row-${i}`}>
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-semibold truncate text-[var(--bhega-text)]">{it.product_name}{cityShort ? ` · ${cityShort}` : ""}{it.date ? ` · ${fmtDate(it.date)}` : ""}</div>
        <div className="text-xs text-muted-foreground mt-0.5">Exited {fmtDate(it.exited_at)} · &ldquo;{reasonLabel}&rdquo;</div>
      </div>
      <Link to={`/${it.product_slug || it.product_id}`} className="btn-outline text-xs shrink-0" data-testid={`profile-exited-link-${i}`}>Re-join?</Link>
    </div>
  );
}

export default function MePage() {
  const { user, setUser, city, logout, ready } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [editingName, setEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [active, setActive] = useState(null);
  const [exited, setExited] = useState([]);

  useEffect(() => { setName(user?.name || ""); }, [user]);

  const loadBhega = () => {
    if (!user) return;
    api.get("/me/bhega")
      .then((r) => {
        setActive((r.data.active || r.data.items || []).filter((it) => (it.city || "").trim().length > 0));
        setExited(r.data.exited || []);
      })
      .catch(() => { setActive([]); setExited([]); });
  };

  useEffect(() => {
    loadBhega();
    const onChange = () => loadBhega();
    window.addEventListener("bhega:changed", onChange);
    return () => window.removeEventListener("bhega:changed", onChange);
    // eslint-disable-next-line
  }, [user]);

  if (!ready) return <div className="container-x py-16"><div className="skeleton h-10 w-64" /></div>;
  if (!user) return <Navigate to="/" replace />;

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name can't be empty"); return; }
    setSavingName(true);
    try {
      await api.patch("/me/name", { name: trimmed });
      const next = { ...user, name: trimmed };
      setUser(next);
      localStorage.setItem("bhega_user", JSON.stringify(next));
      toast.success("Name updated");
      setEditingName(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not update name"));
    } finally {
      setSavingName(false);
    }
  };

  const unread = (active || []).reduce((a, r) => a + (r.unread_count || 0), 0);

  return (
    <div className="container-x py-8 sm:py-12 grid lg:grid-cols-[1fr_340px] gap-8 items-start">
      <div className="min-w-0">
        <div className="eyebrow">My rooms</div>
        <h1 className="t-page mt-1" data-testid="profile-hello">Hello, {user.name}</h1>
        <p className="text-muted-foreground mt-1.5">
          {active === null ? "Loading your rooms…" : active.length
            ? <>You&apos;re part of <strong className="text-foreground num">{active.length}</strong> buying group{active.length === 1 ? "" : "s"}{unread ? <> · <strong className="text-signal num">{unread} unread</strong></> : ""}.</>
            : "You haven't joined a buying group yet."}
        </p>

        <h2 className="mt-8 font-extrabold text-xl">Currently joined Bhega</h2>
        <div className="mt-3">
          {active === null ? (
            <div className="space-y-px"><div className="skeleton h-16" /><div className="skeleton h-16" /></div>
          ) : active.length === 0 ? (
            <EmptyState testId="profile-active-empty" title="No rooms yet" body="Find something you plan to buy and hit the ✊ Bhega button to join other buyers." actionLabel="Browse the catalog" actionTo="/browse" />
          ) : (
            <div className="border border-border divide-y divide-border">{active.map((it, i) => <ActiveRow key={`${it.product_id}-${it.scope}`} it={it} i={i} />)}</div>
          )}
        </div>

        <h2 className="mt-10 font-extrabold text-xl flex items-center gap-2"><History size={18} className="text-muted-foreground" /> Previously joined Bhega</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Rooms you&apos;ve exited — with the reason you gave us.</p>
        <div className="mt-3">
          {exited.length === 0 ? (
            <div className="border border-dashed border-[var(--bhega-border-strong)] p-5 text-center text-sm text-muted-foreground" data-testid="profile-exited-empty">No exited rooms yet.</div>
          ) : (
            <div className="border border-border divide-y divide-border">{exited.map((it, i) => <ExitedRow key={`${it.product_id}-${it.scope}-${i}`} it={it} i={i} />)}</div>
          )}
        </div>
      </div>

      <aside id="profile" className="bhega-card lg:sticky lg:top-[100px]" aria-label="Profile">
        <div className="px-5 py-4 border-b border-border font-extrabold">Profile</div>
        <div className="divide-y divide-border">
          <div className="flex items-center gap-3 px-5 py-4">
            <Phone size={16} className="text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="eyebrow">Phone number</div>
              <div className="font-semibold mt-0.5 num" data-testid="profile-phone">{user.phone_number || "—"}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-5 py-4">
            <Edit3 size={16} className="text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="eyebrow">Name</div>
              {editingName ? (
                <div className="flex items-center gap-1.5 mt-1">
                  <input value={name} onChange={(e) => setName(e.target.value)} className="input-bhega flex-1 min-w-0" aria-label="Your name" data-testid="profile-name-input" autoFocus />
                  <button onClick={saveName} disabled={savingName} className="btn-primary w-11 px-0" aria-label="Save name" data-testid="profile-name-save"><Check size={15} /></button>
                  <button onClick={() => { setEditingName(false); setName(user.name); }} className="btn-outline w-11 px-0" aria-label="Cancel" data-testid="profile-name-cancel"><X size={15} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="font-semibold truncate" data-testid="profile-name">{user.name}</span>
                  <button onClick={() => setEditingName(true)} className="text-xs font-bold underline text-muted-foreground hover:text-foreground" data-testid="profile-name-edit">Edit</button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 px-5 py-4">
            <MapPin size={16} className="text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="eyebrow">Current city</div>
              <div className="font-semibold mt-0.5" data-testid="profile-city">{city || <span className="text-muted-foreground font-normal">Not set — pick a city from the header</span>}</div>
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-border">
          <button onClick={logout} className="btn-outline w-full text-[var(--bhega-danger)]" data-testid="profile-logout"><LogOut size={14} /> Sign out</button>
        </div>
      </aside>
    </div>
  );
}
